import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../services/supabase';
import { pushRpc } from '../../services/pushNotifications';

export default function ChildPushSettings({ childId, active }) {
  const [choice, setChoice] = useState(null);
  const [loading, setLoading] = useState(true), [saving, setSaving] = useState(false);
  const [error, setError] = useState(''), [saved, setSaved] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    let disposed = false;
    const refresh = async () => {
      const request = ++generation.current;
      setLoading(true); setError('');
      try {
        const children = await pushRpc(supabase, 'push_child_preferences', {});
        if (!disposed && request === generation.current) setChoice(children.find(child => child.id === childId) || null);
      } catch { if (!disposed && request === generation.current) setError('Unable to load the notification choice. Reopen Manage Child to retry.'); }
      finally { if (!disposed && request === generation.current) setLoading(false); }
    };
    refresh(); window.addEventListener('focus', refresh);
    return () => { disposed = true; ++generation.current; window.removeEventListener('focus', refresh); };
  }, [childId]);
  const enabled = !!choice?.consented && (choice.daily_tip || choice.weekly_reminder);
  const save = async checked => {
    ++generation.current; setLoading(false); setSaving(true); setError(''); setSaved(false);
    try {
      const children = await pushRpc(supabase, 'push_child_preferences', {
        p_child_id: childId, p_consent: checked, p_daily: checked, p_weekly: checked,
      });
      setChoice(children.find(child => child.id === childId) || null); setSaved(true);
    } catch { setError('Unable to confirm the change was saved. Reopen Manage Child to check before trying again.'); }
    finally { setSaving(false); }
  };
  return <section className="mt-4" aria-label="Child notifications">
    <div className={`rounded-xl border p-3 sm:p-4 ${enabled ? 'border-teal-300 bg-teal-50 dark:border-teal-700 dark:bg-teal-950/35' : 'border-gray-200 bg-gray-50 dark:border-slate-600 dark:bg-slate-900/50'}`}>
      <label className="flex items-start gap-3 font-extrabold"><input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-teal-600" checked={enabled} disabled={loading || saving || !choice || !active} onChange={event => save(event.target.checked)} aria-describedby="manage-child-push-explanation" /><span className="min-w-0 flex-1">Enable Daily Tips and Weekly Reminders for my child</span></label>
      <p id="manage-child-push-explanation" className="mt-2 text-sm text-gray-600 dark:text-slate-300">I allow MyPiggy to send notifications to my child’s device. They’ll receive daily money tips and a friendly reminder after seven days without activity. Notifications must also be allowed on the device. No balances, login IDs, or transaction details appear in notifications. You can change this setting anytime.</p>
      {enabled && <p className="mt-2 text-xs text-gray-600 dark:text-slate-300">Your child can turn either type off in their own settings. Currently: Daily Tips {choice.daily_tip ? 'on' : 'off'} · Weekly Reminders {choice.weekly_reminder ? 'on' : 'off'}.</p>}
    </div>
    {loading && <p role="status" className="mt-2 text-sm">Loading notification choice…</p>}
    {!active && <p className="mt-2 text-sm">Notifications are unavailable while this child is inactive.</p>}
    {error && <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-300">{error}</p>}
    {saving && <p role="status" className="mt-2 text-sm">Saving notification choice…</p>}
    {saved && <p role="status" className="mt-2 text-sm text-teal-700 dark:text-teal-300">Notification choice saved.</p>}
  </section>;
}
